import {useInfiniteQuery, useQueryClient} from '@tanstack/react-query';

import {
    UsageNode,
    getHtmlForUsage,
    getNodeIdOfResolvedNode,
    CurrentPage,
    FindUsagePageEntryAdapter,
    findUsages
} from '../../../../../../../webeditor/biokb-editor-adapters';
import {NodeSuggestion} from '../../../../common/sidebar-search-field/interface';

export interface UsageNodes {
    total: number;
    hasNextPage: boolean;
    data: DocumentNode[];
    nextPage: () => Promise<CurrentPage<UsageNode>>;
}

export interface UsageItem {
    document: EditModeDoc;
    nodes: UsageNodes;
}

type NextPageLoader = () => Promise<CurrentPage<FindUsagePageEntryAdapter>>;

export interface FindUsagesPageResponse {
    totalDocuments: number;
    totalNodes: number;
    items: UsageItem[];
    nextPageLoader?: NextPageLoader;
}

export const NODES_PAGE_SIZE = 7;
export const DOC_PAGE_SIZE = 25;

const keyFactory = {
    allQueries: ['find-usages-edit'],
    specificSearchQuery: (searchData: NodeSuggestion) => [
        ...keyFactory.allQueries,
        searchData.documentAccession,
        searchData.nodeId
    ]
};

export async function convertNodes(
    docAccession: string,
    usages: UsageNode[]
): Promise<DocumentNode[]> {
    return Promise.all(
        usages.map(async (usage) => {
            const htmlElement = await getHtmlForUsage(docAccession, usage);

            return {
                id: getNodeIdOfResolvedNode(usage.node),
                label: htmlElement.innerText
            };
        })
    );
}

export async function convertFindUsagesResponse(
    usagesData: CurrentPage<FindUsagePageEntryAdapter>
): Promise<UsageItem[]> {
    return Promise.all(
        usagesData.data.map(async ({document, nodes}) => ({
            document,
            nodes: {
                total: await nodes.total,
                hasNextPage: nodes.current.hasNextPage,
                data: await convertNodes(document.accession, nodes.current.data),
                nextPage: nodes.nextPage.bind(nodes)
            }
        }))
    );
}

export interface FlattenedFindUsagesResponse {
    totalDocuments: number;
    totalNodes: number;
    items: UsageItem[];
    hasNextPage: boolean;
}

export function useFindUsagesInfiniteQuery(params: NodeSuggestion) {
    return useInfiniteQuery<
        FindUsagesPageResponse,
        Error,
        FlattenedFindUsagesResponse,
        readonly unknown[],
        FindUsagesPageResponse | null
    >({
        gcTime: 0,
        queryKey: keyFactory.specificSearchQuery(params),

        initialPageParam: null as FindUsagesPageResponse | null,
        getNextPageParam: (lastPage) => lastPage,
        queryFn: async ({pageParam}) => {
            const isFirstPage = pageParam === null;

            if (isFirstPage) {
                const pageAdapter = await findUsages(
                    params.documentAccession!,
                    params.nodeId!,
                    DOC_PAGE_SIZE,
                    NODES_PAGE_SIZE
                );

                return {
                    totalDocuments: await pageAdapter.total,
                    totalNodes: await pageAdapter.totalNodes,
                    items: await convertFindUsagesResponse(pageAdapter.current),
                    nextPageLoader: pageAdapter.current.hasNextPage
                        ? pageAdapter.nextPage.bind(pageAdapter)
                        : undefined
                };
            }

            const currentPage = await pageParam!.nextPageLoader!();

            return {
                totalDocuments: 0, // Not needed for subsequent pages
                totalNodes: 0, // Not needed for subsequent pages
                items: await convertFindUsagesResponse(currentPage),
                nextPageLoader: currentPage.hasNextPage ? pageParam?.nextPageLoader : undefined
            };
        },

        select: (data) => {
            // Flatten pages into a single response for backward compatibility with existing components
            const allItems = data.pages.flatMap((page) => page.items);
            const firstPage = data.pages[0];

            return {
                totalDocuments: firstPage.totalDocuments,
                totalNodes: firstPage.totalNodes,
                items: allItems,
                hasNextPage: data.pages[data.pages.length - 1].nextPageLoader !== undefined
            };
        }
    });
}

export function useFindUsagesQueryCache() {
    const queryClient = useQueryClient();

    function updateDocument(document: EditModeDoc) {
        queryClient.setQueriesData<{pages: FindUsagesPageResponse[]} | undefined>(
            {queryKey: keyFactory.allQueries},
            (currentState) => {
                if (currentState) {
                    return {
                        ...currentState,
                        pages: currentState.pages.map((page) => ({
                            ...page,
                            items: page.items.map((usage) =>
                                usage.document.accession === document.accession
                                    ? {...usage, document}
                                    : usage
                            )
                        }))
                    };
                }
            }
        );
    }

    return {updateDocument};
}
